import { useRef, useEffect, useState, useContext } from "react"
import { motion, useInView } from "framer-motion"
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import { graphql } from "gatsby"
import get from "lodash.get"
import { getLessonsData } from "utils/prismic-lessons"
import { NavBar } from "components/organisms/materials/NavBar"
import { Lectures } from "components/organisms/materials/Lectures"
import { siteCountry } from "utils/env"
import OnboardingSection from "components/molecules/onboarding"
import { Container } from "components/atoms/materials/Container"
import { SectionItem } from "components/molecules/materials/SectionItem"
import { Curicculum } from "components/molecules/materials/Curicculum"
import Divided from "components/organisms/common/Divided"
import Button from "components/buttons/Button"
import Contact from "components/organisms/somebuddy/Contact"
import SubscribeForm from "components/SubscribeForm"
import { LocaleContext } from "contexts"
import SEO from "components/seo"
import FormattedTranslation from "components/atoms/FormattedTranslation"
import { handleLinks } from "utils/handleLinks"
import i18nConfig from "../../../config/i18n"

interface SectionProps {
  id: string
  title: string
  description?: string
  lectures: Lecture[]
  children?: React.ReactNode
  showContactButton?: boolean
}

const Section = ({
  id,
  title,
  description,
  lectures,
  children,
  showContactButton,
}: SectionProps) => {
  return (
    <section
      id={id}
      aria-labelledby={title}
      className="my-6 scroll-mt-14 pt-8 sm:my-8 sm:scroll-mt-14 sm:pt-10 lg:my-12 lg:scroll-mt-24 lg:pt-16"
    >
      <div className="space-y-10">
        <Container size="lg">
          <p className="mb-0 mt-8 font-display text-4xl font-bold tracking-tight text-slate-900">
            {title}
          </p>
          {description ? (
            <p className="mb-0 mt-4 text-lg tracking-tight text-slate-700">
              {description}
            </p>
          ) : null}
        </Container>
        {children}
        <Lectures
          lectures={id === "other" ? lectures.slice(1) : lectures}
          sectionId={id}
          showContactButton={showContactButton}
        />
      </div>
    </section>
  )
}

const CTA = ({
  title,
  description,
  button1Text,
  link1,
  button2Text = "",
  link2 = "",
}) => (
  <div className="mt-20 bg-kkm-blue pb-12 pt-12">
    <Container size="lg">
      <div className="grid grid-cols-1 items-end gap-y-4 lg:grid-cols-5 ">
        <div className="col-span-2 lg:mr-8">
          <h2 className="m-0 whitespace-pre-line text-2xl font-extrabold tracking-tight text-kkm-yellow/90 md:text-3xl lg:text-left lg:text-5xl">
            {title}
          </h2>
        </div>
        <div className="col-span-2 flex h-full flex-col justify-between space-y-5 lg:space-y-3">
          <p className="mb-0 text-lg text-slate-50">{description}</p>
          <div className="inline-flex space-x-4">
            <Button link={link1} label={button1Text} bgStyle="yellow" />
            {button2Text ? (
              <Button link={link2} label={button2Text} bgStyle="yellow" />
            ) : null}
          </div>
        </div>
      </div>
    </Container>
  </div>
)

export default function SchoolMaterials({ data }) {
  const { locale } = useContext(LocaleContext)
  const countryOptions = i18nConfig[siteCountry]
  const localeOptions = countryOptions[locale]
  const showNewsletterSubscription = get(
    localeOptions,
    "showNewsletterSubscription",
  )

  // Get all translations for fallback support
  const translations = useTranslations()

  // Get lesson data from Prismic with fallback to translations
  const lessonsData = getLessonsData(data, translations)

  // Use lesson data from Prismic/translations
  const { sections, pageData } = lessonsData
  const {
    title: materialsTitle,
    description: materialsDescription,
    introText,
    contactLinkText: materialsIntroLink,
    curriculum,
  } = pageData

  const ref = useRef(null)
  const isInView = useInView(ref)

  const [showSectionBoxes, setShowSectionBoxes] = useState(false)

  useEffect(() => {
    if (isInView) setShowSectionBoxes(true)
  }, [isInView])

  const HeroImage = ({ className }) => (
    <GatsbyImage
      alt="Trainings hero"
      loading="eager"
      image={getImage(data.schoolTrainingsHero)}
      className={className}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "top",
      }}
    />
  )

  const CurriculumImage = () => (
    <GatsbyImage
      alt="Trainings hero"
      loading="eager"
      image={getImage(data.schoolCurriculum)}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "top",
      }}
    />
  )

  const [mainSections, otherSection] = sections.reduce(
    (acc, s) => (s.id === "other" ? (acc[1] = s) : acc[0].push(s), acc),
    [[], undefined],
  )

  const variants = {
    open: {
      opacity: 1,
      transition: { staggerChildren: 0.07, delayChildren: 0.5 },
    },
    hidden: {
      opacity: 0,
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  }

  return (
    <>
      <SEO title={materialsTitle} />
      <Divided
        Image={HeroImage}
        title={materialsTitle}
        theme="yellow"
        className="mt-20 py-20"
        noVerticalPadding
      >
        <p className="m-0 whitespace-pre-line text-left font-display text-2xl text-zinc-800 sm:text-2xl">
          {materialsDescription}
        </p>
      </Divided>
      <Container
        size="lg"
        className="mb-10 mt-20 flex flex-row items-start lg:mb-16"
      >
        <motion.ul
          variants={variants}
          className="hidden pr-10 sm:block"
          animate={showSectionBoxes ? "open" : "hidden"}
          ref={ref}
          initial="hidden"
        >
          {[
            ...mainSections.map((s) => ({
              id: s.id,
              title: s.title,
              color: s.color,
            })),
            ...(otherSection
              ? [
                  {
                    id: otherSection.id,
                    title: otherSection.title,
                    color: otherSection.color || "persona-color-default",
                  },
                ]
              : []),
          ].map((s) => (
            <SectionItem key={s.id} title={s.title} id={s.id} color={s.color} />
          ))}
        </motion.ul>
        <div className="prose md:max-w-2xl">
          <p>{handleLinks(introText[0])}</p>
          <p>{introText[1]}</p>
          <p>
            {introText[2]}
            <a href="#contact">{materialsIntroLink}</a>
            {introText[3]}.
          </p>
        </div>
      </Container>
      <Curicculum title={curriculum.title} image={CurriculumImage}>
        <div className="prose text-slate-900">
          {curriculum.items.slice(0, 2).map((item, index) => (
            <p key={index} className={index === 1 ? "font-bold" : ""}>
              {item}
            </p>
          ))}
          <ul className="list-disc text-sm ">
            {curriculum.items.slice(2).map((item, index) => (
              <li key={index}>
                <FormattedTranslation html={item} />
              </li>
            ))}
          </ul>
        </div>
      </Curicculum>
      {(() => {
        const sectionsWithFlags = mainSections.map((s) => ({
          ...s,
          showContactButton: siteCountry === "se",
        }))

        const navItemsBase = sectionsWithFlags.map((s) => ({
          id: s.id,
          title: s.title,
        }))
        const otherSectionNavItem = otherSection
          ? {
              id: otherSection.id,
              title: otherSection.title,
            }
          : null

        const navSections = mainSections
          .map((s) => ({ id: s.id, title: s.title }))
          .concat(otherSectionNavItem ? [otherSectionNavItem] : [])

        return (
          <>
            <NavBar sections={navSections} />
            {sectionsWithFlags.map((section) => (
              <Section
                key={section.id}
                id={section.id}
                title={section.title}
                description={section.description}
                lectures={section.lectures}
                showContactButton={section.showContactButton}
              />
            ))}
            {/* Other videos section */}
            {otherSection && (
              <Section
                id={otherSection.id}
                title={otherSection.title}
                description={otherSection.description}
                lectures={otherSection.lectures}
              >
                {siteCountry !== "se" && (
                  <OnboardingSection
                    id="onboarding-video"
                    title={otherSection.lectures[0]?.title}
                    descriptions={[otherSection.lectures[0]?.description]}
                    videoLink={`https://www.youtube-nocookie.com/embed/${otherSection.lectures[0]?.videoLink}`}
                    theme="blue"
                    size="small"
                    buttonAction="openVideo"
                  />
                )}
              </Section>
            )}
            {showNewsletterSubscription ? (
              <SubscribeForm segment="schools" />
            ) : null}
            <Contact />
          </>
        )
      })()}
    </>
  )
}

export const pageQuery = graphql`
  query SchoolMaterialsPage($locale: String!) {
    allPrismicLesson(
      filter: { lang: { eq: $locale } }
      sort: { data: { order: ASC } }
    ) {
      nodes {
        id
        data {
          title {
            text
          }
          description {
            raw
          }
          video_id
          grade_group
          order
          duration
          status
          free
          cover {
            gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
          }
        }
      }
    }

    # Everything selected here is published verbatim in page-data.json, so PDF
    # URLs are fetched only for free lessons. Gated lesson URLs must never enter
    # the payload — hiding the link at render time does not remove it from page data.
    freeLessonPdfs: allPrismicLesson(
      filter: { lang: { eq: $locale }, data: { free: { eq: true } } }
    ) {
      nodes {
        id
        data {
          pdf_link {
            url
          }
        }
      }
    }

    allPrismicGradeGroup(
      filter: { lang: { eq: $locale } }
      sort: { data: { order: ASC } }
    ) {
      nodes {
        id
        data {
          title {
            text
          }
          slug
          description {
            text
          }
          color_theme
          order
        }
      }
    }

    schoolTrainingsHero: file(
      relativePath: { eq: "schools/pexels-fox-1595391.jpeg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 90)
      }
    }
    schoolCurriculum: file(
      relativePath: { eq: "schools/pexels-fauxels-3184638.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 90)
      }
    }
  }
`
